import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useAxios } from '@/composables/useAxios'
import { handleAxiosError } from '@/composables/useAxiosError'
import { STATUS_CODES } from '@/constants'
import type { JwtContent } from '@/types'
import {
  Delegation,
  ManageableDomain,
  AccountRole,
  PermissionCheckResult,
  DelegationPreset
} from '@/types/rbac'

export const usePermissionsStore = defineStore('permissions', () => {
  const roles = ref<string[]>([])
  const permissions = ref<string[]>([])
  const delegations = ref<Delegation[]>([])
  const manageableDomains = ref<ManageableDomain[]>([])
  const delegationPresets = ref<DelegationPreset[]>([])
  const accountRoles = ref<AccountRole[]>([])

  const loading = ref(false)
  const delegationsLoaded = ref(false)
  const manageableDomainsLoaded = ref(false)

  const isAdmin = computed(() =>
    roles.value.includes('admin') || roles.value.includes('super_admin')
  )

  const isSuperAdmin = computed(() => roles.value.includes('super_admin'))

  function initFromJWT(jwtContent: JwtContent) {
    roles.value = jwtContent.roles || []
    permissions.value = jwtContent.perms || []

    // Backward compatibility: if old role field exists and roles is empty
    if ((!jwtContent.roles || jwtContent.roles.length === 0) && jwtContent.role) {
      roles.value = [jwtContent.role]
    }
  }

  function clearPermissions() {
    roles.value = []
    permissions.value = []
    delegations.value = []
    manageableDomains.value = []
    accountRoles.value = []
    delegationsLoaded.value = false
    manageableDomainsLoaded.value = false
  }

  function hasPermission(permission: string): boolean {
    if (isAdmin.value) return true
    return permissions.value.includes(permission)
  }

  function hasAnyPermission(perms: string[]): boolean {
    if (isAdmin.value) return true
    return perms.some((p) => permissions.value.includes(p))
  }

  function hasAllPermissions(perms: string[]): boolean {
    if (isAdmin.value) return true
    return perms.every((p) => permissions.value.includes(p))
  }

  function canManageDomain(domainId: number): boolean {
    if (isAdmin.value) return true
    return manageableDomains.value.some((d) => d.domain_id === domainId)
  }

  function isOwner(domainId: number): boolean {
    const domain = manageableDomains.value.find((d) => d.domain_id === domainId)
    return domain?.is_owner ?? false
  }

  function getDomainPermissions(domainId: number): string[] {
    const allPermissions = ['dns:read', 'dns:write', 'dyndns:read', 'dyndns:write',
      'mailbox:read', 'mailbox:write', 'mailbox:delete',
      'mailinglist:read', 'mailinglist:write']
    if (isAdmin.value) {
      return allPermissions
    }
    const domain = manageableDomains.value.find((d) => d.domain_id === domainId)
    if (domain?.permissions.includes('*')) {
      return allPermissions
    }
    return domain?.permissions ?? []
  }

  function hasDomainPermission(domainId: number, permission: string): boolean {
    if (isAdmin.value) return true
    const domainPerms = getDomainPermissions(domainId)
    return domainPerms.includes(permission)
  }

  async function fetchDelegations(): Promise<void> {
    if (delegationsLoaded.value) return

    loading.value = true
    try {
      const res = await useAxios<null, Delegation[]>('GET', '/account/delegations', true)
      if (res?.status === STATUS_CODES.OK) {
        const validated = Delegation.array().safeParse(res.data)
        if (validated.success) {
          delegations.value = validated.data
          delegationsLoaded.value = true
        }
      }
    } catch (error) {
      handleAxiosError(error, { logPrefix: 'PermissionsStore:fetchDelegations' })
    } finally {
      loading.value = false
    }
  }

  async function fetchManageableDomains(): Promise<void> {
    if (manageableDomainsLoaded.value) return

    loading.value = true
    try {
      const res = await useAxios<null, ManageableDomain[]>('GET', '/account/manageable-domains', true)
      if (res?.status === STATUS_CODES.OK) {
        const validated = ManageableDomain.array().safeParse(res.data)
        if (validated.success) {
          manageableDomains.value = validated.data
          manageableDomainsLoaded.value = true
        }
      }
    } catch (error) {
      handleAxiosError(error, { logPrefix: 'PermissionsStore:fetchManageableDomains' })
    } finally {
      loading.value = false
    }
  }

  async function fetchAccountRoles(): Promise<AccountRole[]> {
    loading.value = true
    try {
      const res = await useAxios<null, AccountRole[]>('GET', '/account/roles', true)
      if (res?.status === STATUS_CODES.OK) {
        const validated = AccountRole.array().safeParse(res.data)
        if (validated.success) {
          accountRoles.value = validated.data
          return validated.data
        }
      }
    } catch (error) {
      handleAxiosError(error, { logPrefix: 'PermissionsStore:fetchAccountRoles' })
    } finally {
      loading.value = false
    }
    return []
  }

  async function fetchDelegationPresets(): Promise<DelegationPreset[]> {
    if (delegationPresets.value.length > 0) {
      return delegationPresets.value
    }

    try {
      const res = await useAxios<null, Record<string, string[]>>(
        'GET',
        '/domain/delegation-presets',
        true
      )
      if (res?.status === STATUS_CODES.OK && res.data) {
        const presets: DelegationPreset[] = Object.entries(res.data).map(
          ([name, permissions]) => ({
            name,
            label: name
              .split('_')
              .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
              .join(' '),
            permissions
          })
        )
        delegationPresets.value = presets
        return presets
      }
    } catch (error) {
      handleAxiosError(error, { logPrefix: 'PermissionsStore:fetchDelegationPresets' })
    }
    return []
  }

  async function refreshPermissions(): Promise<void> {
    loading.value = true
    try {
      const res = await useAxios<null, { roles: string[]; permissions: string[]; is_admin: boolean }>(
        'GET',
        '/account/permissions',
        true
      )
      if (res?.status === STATUS_CODES.OK && res.data) {
        roles.value = res.data.roles
        permissions.value = res.data.permissions
      }
    } catch (error) {
      handleAxiosError(error, { logPrefix: 'PermissionsStore:refreshPermissions' })
    } finally {
      loading.value = false
    }
  }

  async function checkPermission(
    domainId: number,
    permission: string
  ): Promise<PermissionCheckResult> {
    try {
      const res = await useAxios<null, PermissionCheckResult>(
        'GET',
        `/account/check-permission?domain_id=${domainId}&permission=${encodeURIComponent(permission)}`,
        true
      )
      if (res?.status === STATUS_CODES.OK) {
        const validated = PermissionCheckResult.safeParse(res.data)
        if (validated.success) {
          return validated.data
        }
      }
    } catch (error) {
      handleAxiosError(error, { logPrefix: 'PermissionsStore:checkPermission' })
    }
    return { allowed: false, reason: 'Check failed' }
  }

  function invalidateDelegations() {
    delegationsLoaded.value = false
  }

  function invalidateManageableDomains() {
    manageableDomainsLoaded.value = false
  }

  return {
    roles,
    permissions,
    delegations,
    manageableDomains,
    delegationPresets,
    accountRoles,
    loading,

    isAdmin,
    isSuperAdmin,

    initFromJWT,
    clearPermissions,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    canManageDomain,
    isOwner,
    getDomainPermissions,
    hasDomainPermission,

    fetchDelegations,
    fetchManageableDomains,
    fetchAccountRoles,
    fetchDelegationPresets,
    refreshPermissions,
    checkPermission,
    invalidateDelegations,
    invalidateManageableDomains
  }
})
